import useFetchConfig from './useFetchConfig';
import useInteractions from './useInteractions';

import { recordsMatch } from '@frontendmentorio/design-system-v2';

export default function useComments() {
  const { mutate, mutateInfinite } = useFetchConfig();
  const { removeInteractions, setInteractions } = useInteractions();

  const addComment = (collection, comment) => {
    if (!collection) return;

    if (!comment.parent) return [comment].concat(collection);

    return collection.map((item) => {
      if (recordsMatch(item, comment.parent)) {
        return { ...item, replies: (item.replies || []).concat(comment) };
      }

      return item;
    });
  };

  const removeComment = (collection, comment) => {
    if (!collection) return;

    const updated = collection.map((item) => {
      if (recordsMatch(item, comment)) return { ...item, content: '' };

      if (item?.replies?.length) {
        return {
          ...item,
          replies: item.replies.filter((item) => !recordsMatch(item, comment)),
        };
      }

      return item;
    });

    return updated.filter((item) => item.content || item?.replies?.length);
  };

  const updateComment = (collection, comment) => {
    if (!collection) return;

    const { content, isHelpful, upvotes } = comment;

    return collection.map((item) => {
      if (recordsMatch(item, comment)) {
        return { ...item, content, isHelpful, upvotes };
      }

      if (item?.replies?.length) {
        return { ...item, replies: updateComment(item.replies, comment) };
      }

      return item;
    });
  };

  const mutateCache = async (updateFn, comment) => {
    const solutionId = comment.solution.id ?? comment.solution;
    const userId = comment.user.id ?? comment.user;

    const updater = async ({ data, ...rest } = {}) => ({
      data: updateFn(data, comment),
      ...rest,
    });

    await mutate(`/api/v3/solutions/${solutionId}/comments`, updater, {
      revalidate: false,
    });

    if (!comment.parent) {
      await mutateInfinite(`/api/v3/comments`, updater, { revalidate: false });
      await mutateInfinite(`/api/v3/learner/comments`, updater, {
        revalidate: false,
      });
      await mutateInfinite(`/api/v3/learners/${userId}/comments`, updater, {
        revalidate: false,
      });
    }
  };

  const create = async (formData) => {
    const res = await fetch('/api/v3/comments', {
      body: JSON.stringify(formData),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    });

    const body = await res.json();

    if (!res.ok) throw new Error(body.message);

    await mutateCache(addComment, body.data);

    return body.data;
  };

  const markHelpful = async (comment) => {
    const res = await fetch(`/api/v3/comments/${comment.id}/helpful`, {
      method: 'POST',
    });

    const body = await res.json();

    if (!res.ok) throw new Error(body.message);

    setInteractions(comment, { isHelpful: body.data.isHelpful });
  };

  const remove = async (comment) => {
    const res = await fetch(`/api/v3/comments/${comment.id}`, {
      method: 'DELETE',
    });

    if (!res.ok) {
      const body = await res.json();
      throw new Error(body.message);
    }

    await mutateCache(removeComment, comment);

    removeInteractions(comment);
  };

  const update = async (comment, formData) => {
    const res = await fetch(`/api/v3/comments/${comment.id}`, {
      body: JSON.stringify(formData),
      headers: { 'Content-Type': 'application/json' },
      method: 'PUT',
    });

    const body = await res.json();

    if (!res.ok) throw new Error(body.message);

    await mutateCache(updateComment, body.data);
  };

  const upvote = async (comment) => {
    const res = await fetch(`/api/v3/comments/${comment.id}/upvote`, {
      method: 'POST',
    });

    const body = await res.json();

    if (!res.ok) throw new Error(body.message);

    setInteractions(comment, { upvotes: body.data.upvotes });
  };

  return {
    create,
    markHelpful,
    remove,
    update,
    upvote,
  };
}
